import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Card from '../UI/Card';
import Button from '../UI/Button';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import AddAdminModal from './AddAdminModal';
import EditAdminModal from './EditAdminModal';
import adminService, { Admin } from '../../api/services/admin.service';
import authService from '../../api/services/auth.service';
import {
  ADMIN_ROLES,
  ADMIN_ROLE_LABELS,
  AdminRole,
} from '../../constants/adminRoles';

const AdminManagement: React.FC = () => {
  const queryClient = useQueryClient();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  const itemsPerPage = 10;
  const currentAdmin = authService.getCurrentAdmin();

  /* =========================================================
     ADMINS QUERY
  ========================================================= */
  const {
    data,
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      'admins',
      currentPage,
      itemsPerPage,
      searchTerm,
      statusFilter,
      roleFilter,
    ],
    queryFn: () =>
      adminService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined,
      }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const admins = data?.admins || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: itemsPerPage,
    total: 0,
    pages: 0,
  };

  const loadingTable = isFetching && !isLoading;

  /* =========================================================
     HANDLERS
  ========================================================= */
  const handleSearchSubmit = () => {
    setSearchTerm(localSearch);
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleToggleStatus = async (adminId: string) => {
    try {
      await adminService.toggleStatus(adminId);
      queryClient.refetchQueries({ queryKey: ['admins'] });
    } catch (err) {
      console.error('Error toggling admin status:', err);
      setError('Failed to update admin status');
    }
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await adminService.delete(adminId);
        queryClient.refetchQueries({ queryKey: ['admins'] });
      } catch (err) {
        console.error('Error deleting admin:', err);
        setError('Failed to delete admin');
      }
    }
  };

  const handleEditAdmin = (admin: Admin) => {
    setSelectedAdmin(admin);
    setIsEditModalOpen(true);
  };

  const handleAddAdmin = () => {
    queryClient.refetchQueries({ queryKey: ['admins'] });
  };

  const handleUpdateAdmin = () => {
    queryClient.refetchQueries({ queryKey: ['admins'] });
  };

  const handleClearFilters = () => {
    setLocalSearch('');
    setSearchTerm('');
    setStatusFilter('all');
    setRoleFilter('all');
    setCurrentPage(1);
  };

  const getStatusColor = (status: string) => {
    return status === 'active'
      ? 'bg-green-100 text-green-700'
      : 'bg-gray-100 text-gray-700';
  };

  const getRoleColor = (role: AdminRole): string => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-700';
      case 'admin':
        return 'bg-blue-100 text-blue-700';
      case 'finance_manager':
        return 'bg-emerald-100 text-emerald-700';
      case 'operations_manager':
        return 'bg-orange-100 text-orange-700';
      case 'management_viewer':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getPageNumbers = (): number[] => {
    const pageNumbers: number[] = [];
    const total = pagination.pages;
    const current = currentPage;

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pageNumbers.push(i);
    } else if (current <= 3) {
      for (let i = 1; i <= 5; i++) pageNumbers.push(i);
    } else if (current >= total - 2) {
      for (let i = total - 4; i <= total; i++) pageNumbers.push(i);
    } else {
      for (let i = current - 2; i <= current + 2; i++) pageNumbers.push(i);
    }
    return pageNumbers;
  };

  /* =========================================================
     LOADING / ERROR STATES
  ========================================================= */
  if (isLoading && admins.length === 0) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-secondary-500 border-t-transparent"></div>
            <p className="mt-2 text-sm text-gray-500">Loading admins...</p>
          </div>
        </div>
      </Card>
    );
  }

  if (queryError && admins.length === 0) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <p className="text-gray-700">
              {queryError instanceof Error
                ? queryError.message
                : 'Failed to load admins'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Retry
            </button>
          </div>
        </div>
      </Card>
    );
  }

  const hasFilters =
    !!searchTerm || statusFilter !== 'all' || roleFilter !== 'all';

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Admin Management
        </h2>
        <Button size="sm" onClick={() => setIsAddModalOpen(true)}>
          <PlusIcon className="w-4 h-4 mr-1.5" />
          Add Admin
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by name, username, or email..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              onClick={handleSearchSubmit}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
          </div>

          {/* Role filter */}
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Roles</option>
            {ADMIN_ROLES.map((role) => (
              <option key={role} value={role}>
                {ADMIN_ROLE_LABELS[role]}
              </option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {hasFilters && (
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-secondary-500 hover:text-secondary-600 font-medium transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Clear Filters
            </button>
          )}
        </div>
        <div className="text-sm text-gray-500 whitespace-nowrap">
          Showing{' '}
          {admins.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
          {Math.min(currentPage * itemsPerPage, pagination.total)} of{' '}
          {pagination.total} admins
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loadingTable ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Full Name
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Username
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Email / Phone
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Role
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {admins.length > 0 ? (
                admins.map((admin) => (
                  <tr
                    key={admin.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-2.5 px-3 text-sm font-medium text-gray-900">
                      {admin.fullName}
                    </td>
                    <td className="py-2.5 px-3 text-sm text-gray-600">
                      @{admin.username}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <EnvelopeIcon className="w-3.5 h-3.5 text-gray-400" />
                          <span>{admin.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <PhoneIcon className="w-3.5 h-3.5 text-gray-400" />
                          <span>{admin.phoneNumber}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getRoleColor(
                          admin.role
                        )}`}
                      >
                        {ADMIN_ROLE_LABELS[admin.role]}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(
                          admin.status
                        )}`}
                      >
                        {admin.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleStatus(admin.id)}
                          className={`text-xs font-medium px-2 py-1 rounded transition-colors ${
                            admin.status === 'active'
                              ? 'text-yellow-600 hover:bg-yellow-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                        >
                          {admin.status === 'active'
                            ? 'Deactivate'
                            : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleEditAdmin(admin)}
                          className="text-secondary-500 hover:text-secondary-600 text-xs font-medium px-2 py-1 rounded hover:bg-secondary-50 transition-colors"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAdmin(admin.id)}
                          className={`text-red-500 hover:text-red-600 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors ${
                            admin.id === currentAdmin?.id
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          disabled={admin.id === currentAdmin?.id}
                          title={
                            admin.id === currentAdmin?.id
                              ? 'Cannot delete your own account'
                              : ''
                          }
                        >
                          <TrashIcon
                            className={`w-4 h-4 ${
                              admin.id === currentAdmin?.id
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            }`}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center text-sm text-gray-500"
                  >
                    No admins found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && !loadingTable && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="text-sm text-gray-500">
            Page {currentPage} of {pagination.pages}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.max(1, prev - 1))
              }
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Previous
            </button>
            {getPageNumbers().map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  currentPage === pageNum
                    ? 'bg-primary-500 text-white shadow-md'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {pageNum}
              </button>
            ))}
            <button
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(pagination.pages, prev + 1)
                )
              }
              disabled={currentPage === pagination.pages}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
            >
              Next
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add Admin Modal */}
      <AddAdminModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddAdmin}
      />

      {/* Edit Admin Modal */}
      {selectedAdmin && (
        <EditAdminModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAdmin(null);
          }}
          adminData={selectedAdmin}
          onSuccess={handleUpdateAdmin}
        />
      )}
    </Card>
  );
};

export default AdminManagement;